// src/components/OfficeRsvForm.js
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Box,
  Button,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  VStack,
  Grid,
  GridItem,
  HStack,
  FormErrorMessage,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../themes/TableStyles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFileLines,
  faSyringe,
  faTable,
  faSyringe as faSyringeIcon,
  faMoneyBillWave,
  faDollarSign,
  faTrash,
  faCalculator,
  faListOl,
  faGears,
  faCapsules,
} from "@fortawesome/free-solid-svg-icons";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import CustomDatePicker from "./CustomDatePicker";
import * as IconButton from "../themes/IconButtonSet";

const OfficeRsvForm = () => {
  const navigate = useNavigate();
  const { reservationId } = useParams();
  const location = useLocation();
  const { sessionId, profile } = useAuth();
  const [formData, setFormData] = useState({
    ReservationID: reservationId || "XXX-XXXX-XX-XX",
    ContactName:
      [profile?.FirstName, profile?.LastName].filter(Boolean).join(' ') || "",
    ContactTel: profile?.Phone || "",
    ContactEmail: profile?.Email || "",
    UsageObjective: "",
    DeliveryPeriod: "",
    PickupPoint: "",
    OfficeID: profile?.OfficeID || "",
    OfficeName: "",
    ReservedBy: profile?.Email || "",
    ReservationStatus: "ใบจองใหม่",
    ConfirmationEmailSent: false,
    ConsentGiven: true,
    Remark: "",
    Status: "Active",
    VaccineDetails: [],
    documentDate: new Date(),
  });

  const [cycles, setCycles] = useState([]);
  const [pickupPoints, setPickupPoints] = useState([]);
  const [offices, setOffices] = useState([]);
  const [newVaccine, setNewVaccine] = useState({ id: "", unitreq: "" });
  const [vaccineOptions, setVaccineOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(!!reservationId);
  const [errors, setErrors] = useState({});
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const cancelRef = useRef();

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const fetchCycles = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineOrderCycle",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setCycles([response]);
        if (!reservationId) {
          setFormData((prev) => ({
            ...prev,
            DeliveryPeriod: response.CycleName || "",
          }));
        }
      }
    } catch (error) {
      console.error("Error fetching cycles:", error);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getDLDOfficeList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const officeList = response.OfficeList || [];
        setOffices(officeList);
        console.log("Fetched Offices:", officeList);
        return officeList;
      }
      return [];
    } catch (error) {
      console.error("Error fetching offices:", error);
      return [];
    }
  };

  const fetchPickupPoints = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePickupPointList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setPickupPoints(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching pickup points:", error);
    }
  };

  const fetchVaccineOptions = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccinesList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setVaccineOptions(response.VaccinesList || []);
        return response.VaccinesList || [];
      }
      return [];
    } catch (error) {
      console.error("Error fetching vaccines:", error);
      return [];
    }
  };

  const fetchReservation = async (id, vaccines, officeList) => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineOrdersByID",
        data: { ReservationID: id },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const reservation = response.VaccineReservation;
        const details = response.VaccineReservationDetails || [];
        const updatedDetails = details.map((detail) => {
          const vaccine = vaccines.find(
            (v) => v.VaccineID === detail.VaccineID
          );
          const packageSize = vaccine?.PackageSize || 1;
          const doseCount = Math.ceil(detail.RequestQuantity / packageSize);
          return {
            VaccineID: detail.VaccineID,
            name: vaccine ? vaccine.VaccineNameTH : "ไม่พบชื่อวัคซีน",
            LotID: detail.LotID,
            RequestQuantity: detail.RequestQuantity,
            RequestDoses: detail.RequestDoses,
            ApprovedQuantity: detail.ApprovedQuantity,
            ConfirmedQuantity: detail.ConfirmedQuantity,
            UnitPrice: detail.UnitPrice,
            DiscountPercent: detail.DiscountPercent,
            DiscountTotal: detail.DiscountTotal,
            TotalPrice: detail.TotalPrice,
            Remark: detail.Remark,
            DoseCount: doseCount,
          };
        });
        const officeID = String(
          reservation.PickupPoint || profile?.OfficeID || ""
        ).trim();
        const office = officeList.find(
          (o) => String(o.OfficeID).trim() === officeID
        );
        console.log("Reservation OfficeID:", officeID, "Found Office:", office);
        setFormData((prev) => ({
          ...prev,
          ...reservation,
          ContactName: reservation.FarmerName,
          ContactTel: reservation.FarmerTel,
          ContactEmail: reservation.FarmerEmail,
          OfficeID: officeID,
          OfficeName: office
            ? `${office.OfficeName} (${office.OfficeID})`
            : officeID,
          documentDate: new Date(reservation.ReservationDate),
          VaccineDetails: updatedDetails,
        }));
      }
    } catch (error) {
      console.error("Error fetching reservation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      if (sessionId) {
        await fetchCycles();
        await fetchPickupPoints();
        const officeList = await fetchOffices();
        const vaccines = await fetchVaccineOptions();

        // ตั้งค่า OfficeName เฉพาะครั้งแรก
        const officeID = String(
          formData.OfficeID || profile?.OfficeID || ""
        ).trim();
        const office = officeList.find(
          (o) => String(o.OfficeID).trim() === officeID
        );
        console.log("Initial OfficeID:", officeID, "Found Office:", office);
        setFormData((prev) => {
          // ถ้ามี OfficeName อยู่แล้ว ไม่ต้อง override
          if (prev.OfficeName && prev.OfficeName !== prev.OfficeID) return prev;
          return {
            ...prev,
            OfficeID: officeID,
            OfficeName: office
              ? `${office.OfficeName} (${office.OfficeID})`
              : officeID,
          };
        });

        if (reservationId) {
          await fetchReservation(reservationId, vaccines, officeList);
        }
      }
    };
    loadData();
  }, [sessionId, reservationId, profile?.OfficeID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const handleDateChange = (date) => {
    setFormData((prev) => ({ ...prev, documentDate: date }));
  };

  const handleVaccineInputChange = (e) => {
    const { name, value } = e.target;
    setNewVaccine({ ...newVaccine, [name]: value });
  };

  const handleQuantityChange = (vaccineId, lotId, value) => {
    const newQuantity = value === "" ? 0 : parseInt(value, 10);
    const updatedDetails = formData.VaccineDetails.map((detail) => {
      if (detail.VaccineID === vaccineId && detail.LotID === lotId) {
        const packageSize =
          vaccineOptions.find((v) => v.VaccineID === vaccineId)?.PackageSize ||
          1;
        const newTotalPrice = newQuantity * detail.UnitPrice;
        const newDoseCount = Math.ceil(newQuantity / packageSize);
        return {
          ...detail,
          RequestQuantity: newQuantity,
          TotalPrice: newTotalPrice,
          DoseCount: newDoseCount,
        };
      }
      return detail;
    });
    setFormData((prev) => ({ ...prev, VaccineDetails: updatedDetails }));
  };

  const validateField = (name, value) => {
    let newErrors = { ...errors };
    switch (name) {
      case "DeliveryPeriod":
      case "OfficeID":
      case "ContactName":
      case "ContactTel":
      case "ContactEmail":
        if (!value) {
          newErrors[name] = "กรุณากรอกข้อมูล";
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const isFormValid = () => {
    return formData.DeliveryPeriod && Object.keys(errors).length === 0;
  };

  const handleAddVaccine = () => {
    if (newVaccine.id && newVaccine.unitreq > 0) {
      const selectedVaccine = vaccineOptions.find(
        (v) => v.VaccineID === newVaccine.id
      );
      if (selectedVaccine) {
        const existingVaccine = formData.VaccineDetails.find(
          (v) => v.VaccineID === newVaccine.id
        );
        const uop = selectedVaccine.PriceDistribution || "350";
        const packageSize = selectedVaccine.PackageSize || 1;
        const newQuantity = parseInt(newVaccine.unitreq);
        const newDoseCount = Math.ceil(newQuantity / packageSize);

        if (existingVaccine) {
          setIsAlertOpen(true);
        } else {
          const totalCost = (newQuantity * parseInt(uop)).toString();
          const vaccineData = {
            VaccineID: newVaccine.id,
            name: selectedVaccine.VaccineNameTH,
            LotID: `LT${Date.now().toString().slice(-4)}`,
            RequestQuantity: newQuantity,
            RequestDoses: 0,
            ApprovedQuantity: 0,
            ConfirmedQuantity: 0,
            UnitPrice: parseFloat(uop),
            DiscountPercent: 0,
            DiscountTotal: 0,
            TotalPrice: parseFloat(totalCost),
            Remark: "",
            DoseCount: newDoseCount,
          };
          setFormData((prev) => ({
            ...prev,
            VaccineDetails: [...prev.VaccineDetails, vaccineData],
          }));
          setNewVaccine({ id: "", unitreq: "" });
        }
      }
    }
  };

  const handleAlertConfirm = (action) => {
    const selectedVaccine = vaccineOptions.find(
      (v) => v.VaccineID === newVaccine.id
    );
    const uop = selectedVaccine.PriceDistribution || "350";
    const packageSize = selectedVaccine.PackageSize || 1;
    const newQuantity = parseInt(newVaccine.unitreq);
    const newDoseCount = Math.ceil(newQuantity / packageSize);

    if (action === "add") {
      const updatedDetails = formData.VaccineDetails.map((detail) => {
        if (detail.VaccineID === newVaccine.id) {
          const updatedQuantity = detail.RequestQuantity + newQuantity;
          const newTotalPrice = updatedQuantity * parseFloat(uop);
          const updatedDoseCount = Math.ceil(updatedQuantity / packageSize);
          return {
            ...detail,
            RequestQuantity: updatedQuantity,
            TotalPrice: newTotalPrice,
            DoseCount: updatedDoseCount,
          };
        }
        return detail;
      });
      setFormData((prev) => ({ ...prev, VaccineDetails: updatedDetails }));
    } else if (action === "replace") {
      const updatedDetails = formData.VaccineDetails.map((detail) => {
        if (detail.VaccineID === newVaccine.id) {
          const newTotalPrice = newQuantity * parseFloat(uop);
          return {
            ...detail,
            RequestQuantity: newQuantity,
            TotalPrice: newTotalPrice,
            DoseCount: newDoseCount,
          };
        }
        return detail;
      });
      setFormData((prev) => ({ ...prev, VaccineDetails: updatedDetails }));
    }
    setNewVaccine({ id: "", unitreq: "" });
    setIsAlertOpen(false);
  };

  const handleRemoveVaccine = (row) => {
    setFormData((prev) => ({
      ...prev,
      VaccineDetails: prev.VaccineDetails.filter(
        (v) => v.VaccineID !== row.VaccineID
      ),
    }));
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/createVaccineOrders",
        data: { ...formData, VaccineDetails: [], OfficeID: formData.OfficeID },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setFormData((prev) => ({
          ...prev,
          ReservationID: response.ReservationID,
        }));
        setIsSubmitted(true);
        alert("สร้างใบจองสำเร็จ! เลขที่ใบจอง: " + response.ReservationID);
      }
    } catch (error) {
      console.error("Error creating reservation:", error);
      alert("เกิดข้อผิดพลาดในการสร้างใบจอง: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = async () => {
    if (!isFormValid()) {
      alert("กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน");
      return;
    }
    const date = formData.documentDate;
    const formattedDate = date
      ? `${(date.getMonth() + 1).toString().padStart(2, "0")}/${date
          .getDate()
          .toString()
          .padStart(2, "0")}/${date.getFullYear()}`
      : "";
    const submitData = {
      ...formData,
      documentDate: formattedDate,
      OfficeID: formData.OfficeID,
    };

    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/updateVaccineOrdersByID",
        data: submitData,
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        alert("บันทึกใบจองสำเร็จ!");
        navigate("/officersvlist");
      }
    } catch (error) {
      console.error("API Error:", error.message);
      alert("เกิดข้อผิดพลาดในการบันทึก: " + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelReservation = async () => {
    if (window.confirm("คุณแน่ใจหรือไม่ว่าต้องการยกเลิกการจองนี้?")) {
      setIsLoading(true);
      try {
        const response = await apiService({
          method: "POST",
          url: "/cancelVaccineOrdersByID",
          data: { ReservationID: formData.ReservationID },
          sessionId: sessionId || "",
        });
        if (response.result === "success") {
          alert("ยกเลิกการจองสำเร็จ!");
          navigate("/officersvlist");
        } else {
          alert("เกิดข้อผิดพลาดในการยกเลิก: " + response.error);
        }
      } catch (error) {
        console.error("Error cancelling reservation:", error);
        alert("เกิดข้อผิดพลาดในการยกเลิก: " + error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const isEditable = formData.ReservationStatus === "ใบจองใหม่";

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faListOl} />
          <span>รหัสวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.VaccineID,
      sortable: true,
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faSyringeIcon} />
          <span>ชื่อวัคซีน</span>
        </HStack>
      ),
      selector: (row) => row.name,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCalculator} />
          <span>จำนวน (ขวด)</span>
        </HStack>
      ),
      selector: (row) => row.RequestQuantity,
      sortable: true,
      cell: (row) =>
        isEditable ? (
          <Input
            type="number"
            value={row.RequestQuantity}
            onChange={(e) =>
              handleQuantityChange(row.VaccineID, row.LotID, e.target.value)
            }
            min={0}
            size="sm"
            width="100px"
            ml={10}
          />
        ) : (
          <Box width="100%" ml={10}>
            {row.RequestQuantity.toLocaleString()}
          </Box>
        ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faCapsules} />
          <span>จำนวนโดส</span>
        </HStack>
      ),
      selector: (row) => row.DoseCount,
      sortable: true,
      cell: (row) => (
        <Box width="100%" ml={10}>
          {row.DoseCount.toLocaleString()}
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faMoneyBillWave} />
          <span>ราคาต่อหน่วย</span>
        </HStack>
      ),
      selector: (row) => row.UnitPrice,
      sortable: true,
      cell: (row) => (
        <Box width="100%" ml={10}>
          {row.UnitPrice.toLocaleString()}
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faDollarSign} />
          <span>มูลค่ารวม</span>
        </HStack>
      ),
      selector: (row) => row.TotalPrice,
      sortable: true,
      cell: (row) => (
        <Box width="100%" ml={10}>
          {row.TotalPrice.toLocaleString()}
        </Box>
      ),
      width: "200px",
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faGears} />
          <span>การดำเนินการ</span>
        </HStack>
      ),
      cell: (row) => (
        <IconButton.Delete
          icon={faTrash}
          onClick={() => handleRemoveVaccine(row)}
          label="ลบรายการ"
          width="100px"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "120px",
    },
  ];

  if (!sessionId) {
    return null;
  }

  return (
    <Box p={35} m={25} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <Heading as="h2" mb={10} textAlign="center">
        รายการสั่งจองวัคซีน (สำนักงาน)
      </Heading>

      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
        <Box
          mb={5}
          borderRadius="full"
          w="100%"
          bgColor="iBlue.600"
          color="white"
          p={2}
        >
          <HStack spacing={5} ml={5}>
            <FontAwesomeIcon icon={faFileLines} color="white" size="xl" />
            <Heading size="lg" fontWeight="normal">
              ข้อมูลเอกสารการสั่งจองวัคซีน
            </Heading>
          </HStack>
        </Box>
        <Grid templateColumns="repeat(4, 1fr)" gap={6} p={5}>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                เลขที่ใบสั่งจองวัคซีน
              </FormLabel>
              <Input
                name="ReservationID"
                value={formData.ReservationID}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.DeliveryPeriod}>
              <FormLabel fontSize="xl" color="gray">
                รอบการจัดส่งวัคซีน
              </FormLabel>
              <Select
                name="DeliveryPeriod"
                value={formData.DeliveryPeriod}
                onChange={handleInputChange}
                placeholder="เลือกรอบการจัดส่ง"
                colorScheme="iBlue"
              >
                {cycles.map((cycle) => (
                  <option key={cycle.CycleID} value={cycle.CycleName}>
                    {cycle.CycleName}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.DeliveryPeriod}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                วันที่เอกสาร
              </FormLabel>
              <CustomDatePicker
                value={formData.documentDate}
                onChange={handleDateChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.PickupPoint}>
              <FormLabel fontSize="xl" color="gray">
                จุดจำหน่ายที่ต้องการรับวัคซีน
              </FormLabel>
              <Select
                name="PickupPoint"
                value={formData.PickupPoint}
                onChange={handleInputChange}
                placeholder="เลือกจุดจำหน่าย"
                colorScheme="iBlue"
              >
                {pickupPoints.map((point) => (
                  <option key={point.WarehouseID} value={point.WarehouseID}>
                    {point.WarehouseName}
                  </option>
                ))}
              </Select>
              <FormErrorMessage>{errors.PickupPoint}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.OfficeID}>
              <FormLabel fontSize="xl" color="gray">
                สำนักงาน
              </FormLabel>
              <Input
                name="OfficeName"
                value="สำนักงานปศุสัตว์จังหวัด"
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.OfficeID}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.ContactName}>
              <FormLabel fontSize="xl" color="gray">
                ชื่อผู้ติดต่อ
              </FormLabel>
              <Input
                name="ContactName"
                value={formData.ContactName}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.ContactName}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.ContactTel}>
              <FormLabel fontSize="xl" color="gray">
                เบอร์โทรติดต่อ
              </FormLabel>
              <Input
                name="ContactTel"
                value={formData.ContactTel}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.ContactTel}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={1}>
            <FormControl isRequired isInvalid={!!errors.ContactEmail}>
              <FormLabel fontSize="xl" color="gray">
                E-mail Address
              </FormLabel>
              <Input
                name="ContactEmail"
                value={formData.ContactEmail}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
              <FormErrorMessage>{errors.ContactEmail}</FormErrorMessage>
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl mb={4}>
              <FormLabel fontSize="xl" color="gray">
                วัตถุประสงค์การใช้งาน / รายละเอียดเพิ่มเติม
              </FormLabel>
              <Textarea
                name="UsageObjective"
                value={formData.UsageObjective}
                onChange={handleInputChange}
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
          <GridItem colSpan={4}>
            <FormControl>
              <FormLabel fontSize="xl" color="gray">
                สถานะรายการ
              </FormLabel>
              <Input
                name="ReservationStatus"
                value={formData.ReservationStatus}
                onChange={handleInputChange}
                isReadOnly
                colorScheme="iBlue"
              />
            </FormControl>
          </GridItem>
        </Grid>
        {!isSubmitted && (
          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5} textAlign="right">
              <IconButton.Cancel onClick={() => navigate("/officersvlist")} />
              <IconButton.Submit
                label="สร้างใบจองวัคซีน"
                onClick={handleSubmit}
              />
            </HStack>
          </Box>
        )}
      </Box>

      {isSubmitted && (
        <>
          {isEditable && (
            <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
              <Box
                mb={5}
                borderRadius="full"
                w="100%"
                bgColor="iPurple.500"
                color="white"
                p={2}
              >
                <HStack spacing={5} ml={5}>
                  <FontAwesomeIcon icon={faSyringe} color="white" size="xl" />
                  <Heading size="lg" fontWeight="normal">
                    เลือกวัคซีนที่ต้องการสั่งจอง
                  </Heading>
                </HStack>
              </Box>
              <Grid templateColumns="repeat(3, 1fr)" gap={6} p={5}>
                <FormControl>
                  <FormLabel fontSize="xl" color="gray">
                    เลือกวัคซีน
                  </FormLabel>
                  <Select
                    name="id"
                    value={newVaccine.id}
                    onChange={handleVaccineInputChange}
                    placeholder="เลือกวัคซีน"
                    colorScheme="iPurple"
                  >
                    {vaccineOptions.map((vaccine) => (
                      <option key={vaccine.VaccineID} value={vaccine.VaccineID}>
                        {vaccine.VaccineNameTH}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xl" color="gray">
                    จำนวน
                  </FormLabel>
                  <Input
                    name="unitreq"
                    type="number"
                    value={newVaccine.unitreq}
                    onChange={handleVaccineInputChange}
                    min={1}
                    colorScheme="iPurple"
                  />
                </FormControl>
                <Box mt={10}>
                  <IconButton.Add onClick={handleAddVaccine} h={9} />
                </Box>
              </Grid>
            </Box>
          )}

          <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
            <Box
              mb={5}
              borderRadius="full"
              w="100%"
              bgColor="iOrange.500"
              color="white"
              p={2}
            >
              <HStack spacing={5} ml={5}>
                <FontAwesomeIcon icon={faTable} color="white" size="xl" />
                <Heading size={{ base: "md", md: "lg" }} fontWeight="normal">
                  รายการวัคซีนที่ต้องการสั่งจอง
                </Heading>
              </HStack>
            </Box>
            <Box p={5} overflowX="auto">
              <DataTable
                columns={columns}
                data={formData.VaccineDetails}
                pagination
                highlightOnHover
                striped
                responsive
                customStyles={tableCustomStyles}
              />
            </Box>
          </Box>

          <Box p={5} textAlign="right">
            <HStack spacing={5} mb={5}>
              <IconButton.Back
                onClick={() => navigate("/officersvlist")}
                label="ย้อนกลับ"
              />
              {isEditable && (
                <IconButton.Cancel
                  onClick={handleCancelReservation}
                  label="ยกเลิกการจอง"
                />
              )}
              {isEditable && (
                <IconButton.Save
                  onClick={handleConfirm}
                  isLoading={isLoading}
                />
              )}
            </HStack>
          </Box>

          <AlertDialog
            isOpen={isAlertOpen}
            leastDestructiveRef={cancelRef}
            onClose={() => handleAlertConfirm("cancel")}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize="lg" fontWeight="bold">
                  วัคซีนซ้ำ
                </AlertDialogHeader>
                <AlertDialogBody>
                  มีวัคซีนนี้อยู่ในรายการสั่งจองแล้ว กรุณาเลือกการดำเนินการ:
                </AlertDialogBody>
                <AlertDialogFooter>
                  <Button
                    ref={cancelRef}
                    onClick={() => handleAlertConfirm("cancel")}
                  >
                    ยกเลิก
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={() => handleAlertConfirm("add")}
                    ml={3}
                  >
                    เพิ่มจำนวน
                  </Button>
                  <Button
                    colorScheme="green"
                    onClick={() => handleAlertConfirm("replace")}
                    ml={3}
                  >
                    ปรับจำนวน
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        </>
      )}
    </Box>
  );
};

export default OfficeRsvForm;
